const SIGNABLE_ELEMENT_TYPES = new Set(['SIGNATURE', 'INITIALS']);
const RUNTIME_FIELD_ELEMENT_TYPES = new Set(['SIGNATURE', 'INITIALS', 'TEXT_INPUT', 'DATE_INPUT', 'CHOICE']);
const hasDuplicate = (values) => new Set(values).size !== values.length;
const isRuntimeFieldElement = (type) => RUNTIME_FIELD_ELEMENT_TYPES.has(type);
export const CONSENT_PUBLISH_VALIDATION_MESSAGES = {
    noRoles: 'Add at least one signer role before publishing.',
    roleLabelMissing: 'Each signer role must have a label before publishing.',
    signerTypeMissing: 'Each signer role must have a signer type before publishing.',
    roleKeyMissing: 'Each signer role must have a stable key before publishing.',
    duplicateRoleKeys: 'Signer role keys must be unique before publishing.',
    duplicateRoleLabels: 'Signer role labels must be unique before publishing.',
    fieldKeyMissing: 'Every signature, initials, and data field must have a stable key before publishing.',
    signerRoleMissing: 'Assign a signer role to every signature, initials, and data field before publishing.',
    signerRoleInvalid: 'Each signature, initials, and data field must reference a role defined in the Roles tab.',
    signingOrderMissing: 'Set a valid signing order on every signature and initials field before publishing.',
    signingOrderMismatch: 'Signature and initials signing order must match the signer role order before publishing.',
    duplicateSigningOrders: 'Signer roles cannot share the same signing order before publishing.',
    runtimeFieldsMissing: 'Configure at least one signature, initials, or data field before publishing.',
    duplicateFieldKeys: 'Field keys must be unique before publishing.',
};
export const validateConsentDocumentForPublish = (document) => {
    const normalizedRoles = document.roles
        .map((role) => ({
        ...role,
        role: role.role.trim(),
        signerTypeId: role.signerTypeId.trim(),
    }))
        .filter((role) => role.role.length > 0);
    if (document.roles.length === 0 || normalizedRoles.length === 0) {
        return CONSENT_PUBLISH_VALIDATION_MESSAGES.noRoles;
    }
    if (document.roles.some((role) => role.role.trim().length === 0)) {
        return CONSENT_PUBLISH_VALIDATION_MESSAGES.roleLabelMissing;
    }
    if (normalizedRoles.some((role) => role.signerTypeId.length === 0)) {
        return CONSENT_PUBLISH_VALIDATION_MESSAGES.signerTypeMissing;
    }
    const roleKeys = normalizedRoles.map((role) => role.id.trim());
    if (roleKeys.some((roleKey) => roleKey.length === 0)) {
        return CONSENT_PUBLISH_VALIDATION_MESSAGES.roleKeyMissing;
    }
    if (hasDuplicate(roleKeys)) {
        return CONSENT_PUBLISH_VALIDATION_MESSAGES.duplicateRoleKeys;
    }
    const roleNames = normalizedRoles.map((role) => role.role);
    if (hasDuplicate(roleNames)) {
        return CONSENT_PUBLISH_VALIDATION_MESSAGES.duplicateRoleLabels;
    }
    const roleOrderByName = new Map(normalizedRoles.map((role, index) => [role.role, index + 1]));
    const validRoleNames = new Set(roleOrderByName.keys());
    const runtimeFieldKeys = [];
    const signingRoleByOrder = new Map();
    for (const element of document.elements) {
        if (!isRuntimeFieldElement(element.type)) {
            continue;
        }
        const elementKey = element.config.ec__key?.trim() ?? '';
        if (!elementKey) {
            return CONSENT_PUBLISH_VALIDATION_MESSAGES.fieldKeyMissing;
        }
        runtimeFieldKeys.push(elementKey);
        const signerRole = element.config.ec__signer_role?.trim();
        if (!signerRole) {
            return CONSENT_PUBLISH_VALIDATION_MESSAGES.signerRoleMissing;
        }
        if (!validRoleNames.has(signerRole)) {
            return CONSENT_PUBLISH_VALIDATION_MESSAGES.signerRoleInvalid;
        }
        if (!SIGNABLE_ELEMENT_TYPES.has(element.type)) {
            continue;
        }
        const signingOrder = element.config.ec__order;
        if (typeof signingOrder !== 'number' || !Number.isInteger(signingOrder) || signingOrder < 1) {
            return CONSENT_PUBLISH_VALIDATION_MESSAGES.signingOrderMissing;
        }
        const existingRoleForOrder = signingRoleByOrder.get(signingOrder);
        if (existingRoleForOrder !== undefined && existingRoleForOrder !== signerRole) {
            return CONSENT_PUBLISH_VALIDATION_MESSAGES.duplicateSigningOrders;
        }
        signingRoleByOrder.set(signingOrder, signerRole);
        if (signingOrder !== roleOrderByName.get(signerRole)) {
            return CONSENT_PUBLISH_VALIDATION_MESSAGES.signingOrderMismatch;
        }
    }
    if (runtimeFieldKeys.length === 0) {
        return CONSENT_PUBLISH_VALIDATION_MESSAGES.runtimeFieldsMissing;
    }
    if (hasDuplicate(runtimeFieldKeys)) {
        return CONSENT_PUBLISH_VALIDATION_MESSAGES.duplicateFieldKeys;
    }
    return null;
};
